import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCaretDown,
  faCaretUp,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  Nav,
  Image,
  Button,
  Modal
} from '@themesberg/react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../../redux/actions/auth';
import { Routes } from '../../routes';
import ReactHero from '../../assets/img/state-govt-logo.png';
import ProfilePicture from '../../assets/img/default_avatar.png';
import { mainTreasuryModules } from '../../pages/examples/user-admin/modules';
import { isActivePath } from '../../utils/routeMatch';

const MobileSidebar = ({ show, onHide, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const isPathActive = (link) => isActivePath(location.pathname, link);

  // The audit roles own the audit module, so the screens are reachable even if
  // accessTo has not been backfilled for that account.
  const isAuditor = /^auditor(\s|_)?general$|^auditor$/i.test(
    String(user?.role || '').trim()
  );
  const canOpenModule = (title) =>
    (user?.accessTo && user.accessTo.includes(title)) ||
    (isAuditor && title === 'Auditor General');

  const activeModuleKey = () => {
    const module = mainTreasuryModules.find(
      (m) =>
        (m.subMenu && m.subMenu.some((item) => isPathActive(item.route))) ||
        isPathActive(m.route)
    );
    return module ? module.title.split(" ").join("-") : null;
  };

  const [openDropdowns, setOpenDropdowns] = useState(() => {
    const key = activeModuleKey();
    return key ? [key] : [];
  });

  // Re-open the group that owns the current page whenever the drawer is shown.
  useEffect(() => {
    if (!show) return;
    const key = activeModuleKey();
    if (key) setOpenDropdowns((p) => (p.includes(key) ? p : [...p, key]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, location.pathname]);

  // Bootstrap only locks <body>; the document scroller lives on <html>, so the page
  // behind the drawer could still scroll. Lock the root element while it is open.
  useEffect(() => {
    const root = document.documentElement;
    if (show) root.classList.add('modal-open');
    return () => root.classList.remove('modal-open');
  }, [show]);

  const handleLogout = () => {
    dispatch(logout(() => navigate(Routes.Signin.path)));
    onHide();
  };

  const handleNavigation = (path) => {
    navigate(path);
    onHide();
  };

  const CollapsableNavItem = ({ eventKey, title, icon, children = null, isActive = false }) => {
    const isDropdownOpen = openDropdowns.includes(eventKey);

    const handleDropdownToggle = (eventKey) => {
      const index = openDropdowns.indexOf(eventKey);
      if (index === -1) {
        setOpenDropdowns([eventKey]);
      } else {
        setOpenDropdowns([]);
      }
    };

    return (
      <div className="mb-2">
        <div
          className={`d-flex justify-content-between align-items-center p-3 bg-light rounded${isActive ? ' is-active-group' : ''}`}
          onClick={() => handleDropdownToggle(eventKey)}
          style={{ cursor: 'pointer', minHeight: 48 }}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={icon} className="me-3 text-primary" />
            <span className="fw-semibold">{title}</span>
          </div>
          <FontAwesomeIcon
            icon={isDropdownOpen ? faCaretUp : faCaretDown}
            className="text-muted"
          />
        </div>
        
        {isDropdownOpen && (
          <div className="mt-2 ps-4">
            {children}
          </div>
        )}
      </div>
    );
  };

  const NavItem = ({ title, link, icon }) => {
    const active = isPathActive(link);
    return (
      <div
        role="button"
        tabIndex={0}
        aria-current={active ? 'page' : undefined}
        className={`d-flex align-items-center p-2 mb-1 rounded hover-bg-light${active ? ' is-active' : ''}`}
        onClick={() => handleNavigation(link)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleNavigation(link);
          }
        }}
        style={{ cursor: 'pointer', minHeight: 40 }}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`me-3 ${active ? 'text-primary' : 'text-secondary'}`}
          size="sm"
        />
        <span className={active ? 'fw-semibold text-primary' : undefined}>{title}</span>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="mobile-sidebar-modal"
      contentClassName="mobile-sidebar-content"
      backdrop="static"
      keyboard
    >
      <Modal.Header className="bg-primary text-white border-0">
        <div className="d-flex align-items-center">
          <Image
            src={ReactHero}
            height={40}
            width={40}
            className="me-3"
          />
          <Modal.Title className="fw-bold">KIFMIS</Modal.Title>
        </div>
        <Button
          variant="link"
          className="text-white p-0 border-0 ms-auto"
          onClick={onHide}
          aria-label="Close menu"
          style={{ boxShadow: 'none' }}
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </Button>
      </Modal.Header>

      <Modal.Body className="p-0">
        {/* User Info */}
        <div className="p-3 bg-light border-bottom">
          <div className="d-flex align-items-center">
            <Image
              src={ProfilePicture}
              className="rounded-circle me-3"
              width="40"
              height="40"
            />
            <div>
              <h6 className="mb-0">Hi, {user?.username}</h6>
              <small className="text-muted">{user?.mda_name}</small>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="p-3 mobile-sidebar-nav">
          {mainTreasuryModules.map((module, index) => {
            const modKey = module.title.split(" ").join("-");
            
            if (module.subMenu && module.subMenu.length) {
              if (canOpenModule(module.title)) {
                return (
                  <CollapsableNavItem
                    key={index}
                    eventKey={modKey}
                    title={module.title}
                    icon={module.icon}
                    isActive={
                      module.subMenu.some((item) => isPathActive(item.route)) ||
                      isPathActive(module.route)
                    }
                  >
                    {module.subMenu.map((item, idx) => {
                      const hasFunctionalAccess =
                        (user?.functionalities &&
                          user.functionalities.includes(item.title)) ||
                        (isAuditor && module.title === 'Auditor General');
                      if (hasFunctionalAccess) {
                        const itemKey = `${modKey}-${idx}-${item.title.split(" ").join("-")}`;
                        return (
                          <NavItem
                            key={itemKey}
                            title={item.title}
                            link={item.route}
                            icon={item.icon}
                          />
                        );
                      }
                      return null;
                    })}
                  </CollapsableNavItem>
                );
              }
            } else {
              if (canOpenModule(module.title)) {
                return (
                  <div key={index} className="mb-2">
                    <NavItem
                      title={module.title}
                      link={module.route}
                      icon={module.icon}
                    />
                  </div>
                );
              }
            }
            return null;
          })}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top">
        <Button
          variant="outline-danger"
          className="w-100"
          onClick={handleLogout}
        >
          <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
          Sign Out
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MobileSidebar;